import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { DisputeStatusBadge } from '@/components/disputes/dispute-status-badge';
import { humanizeReason } from '@/components/disputes/humanize-reason';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from '@/lib/money';
import { DisputeDetails } from '@/lib/api/disputes';

interface DisputeOverviewCardProps {
  dispute: DisputeDetails;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-muted-foreground mb-1 text-sm">{label}</span>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
}

export function DisputeOverviewCard({ dispute }: DisputeOverviewCardProps) {
  const { t } = useTranslation('disputes');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('disputeDetails')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Field label={t('table.columns.status')}>
            <DisputeStatusBadge status={dispute.status} />
          </Field>
          <Field label={t('table.columns.reason')}>{humanizeReason(dispute.reason)}</Field>
          <Field label={t('table.columns.amount')}>
            <span className="text-lg font-semibold tabular-nums">
              {formatMoney(dispute.amountMinor, dispute.currency)}
            </span>
          </Field>
          <Field label={t('table.columns.chargeId')}>
            <CopyableResourceBadge name={t('table.columns.chargeId')} value={dispute.chargeId} />
          </Field>
          <Field label={t('table.columns.openedAt')}>
            <DateDisplay value={dispute.openedAt} />
          </Field>
          <Field label={t('table.columns.closedAt')}>
            {dispute.closedAt ? <DateDisplay value={dispute.closedAt} /> : t('details.notAvailable')}
          </Field>
          <Field label={t('table.columns.evidenceDueBy')}>
            {dispute.evidenceDueBy ? <DateDisplay value={dispute.evidenceDueBy} /> : t('details.notAvailable')}
          </Field>
        </div>

        <Separator />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Field label={t('details.providerStatus')}>{dispute.providerStatus || '-'}</Field>
          <Field label={t('details.providerDisputeId')}>
            <CopyableResourceBadge name={t('details.providerDisputeId')} value={dispute.providerDisputeId} />
          </Field>
          <Field label={t('details.paymentIntentId')}>
            {dispute.payfacPaymentIntentId ? (
              <CopyableResourceBadge name={t('details.paymentIntentId')} value={dispute.payfacPaymentIntentId} />
            ) : (
              t('details.notAvailable')
            )}
          </Field>
        </div>

        {dispute.metadata && (
          <>
            <Separator />
            <div>
              <span className="text-muted-foreground mb-2 block text-sm">{t('details.metadata')}</span>
              <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-xs">{dispute.metadata}</pre>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
